import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faUser, faEnvelope, faPhone, faLock } from '@fortawesome/free-solid-svg-icons';
import { registerUser, sendOTP, googleAuth } from '../services/api';
import GoogleAuthButton from '../components/GoogleAuthButton';
import theme from '../styles/theme';

const RegisterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(145deg, ${theme.colors.sidebarFrom} 0%, ${theme.colors.sidebarTo} 45%, #243044 100%);
  position: fixed;
  inset: 0;
  padding: 72px 20px 24px;
  box-sizing: border-box;
  overflow-y: auto;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 60% 50% at 20% 20%, rgba(255, 107, 107, 0.2) 0%, transparent 50%),
      radial-gradient(ellipse 50% 40% at 80% 80%, rgba(255, 142, 83, 0.14) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const HomeLink = styled(Link)`
  position: fixed;
  top: 20px;
  left: 20px;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  padding: 10px 18px;
  border-radius: ${theme.radius.pill};
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(12px);
  transition: all ${theme.transitions.base};
  font-size: ${theme.typography.body};
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: translateY(-2px);
  }
`;

const RegisterCard = styled.div`
  background: ${theme.colors.card};
  padding: 36px 32px;
  border-radius: ${theme.radius.xl};
  box-shadow: ${theme.shadows.xl}, 0 0 0 1px rgba(255, 255, 255, 0.08);
  width: 100%;
  max-width: 460px;
  margin: auto;
  position: relative;
  z-index: 1;
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base};
  animation: slideUp 0.45s cubic-bezier(0.34, 1.2, 0.64, 1);

  @media (min-width: 1024px) {
    max-width: 480px;
    padding: 40px 40px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.xl}, ${theme.shadows.glowSoft};
  }
`;

const Title = styled.h1`
  font-size: ${theme.typography.h1};
  font-weight: 800;
  margin: 0 0 8px;
  text-align: center;
  background: ${theme.colors.accentGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
  letter-spacing: -0.04em;

  @media (min-width: 1024px) {
    font-size: 2rem;
    margin-bottom: 12px;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  color: ${theme.colors.textMuted};
  margin: 0 0 24px;
  font-size: ${theme.typography.body};
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin-bottom: 12px;

  @media (min-width: 1024px) {
    gap: 18px;
    margin-bottom: 16px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const Label = styled.label`
  font-size: ${theme.typography.small};
  font-weight: 600;
  color: ${theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: -0.01em;

  svg {
    color: ${theme.colors.primary};
    font-size: 14px;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px 8px 36px;
  border: 1.5px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  font-size: ${theme.typography.body};
  transition: all ${theme.transitions.base};
  background: ${theme.colors.card};
  box-sizing: border-box;
  height: 44px;
  color: ${theme.colors.text};

  @media (min-width: 1024px) {
    height: 48px;
    padding: 10px 16px 10px 40px;
  }

  &:focus {
    border-color: ${theme.colors.primary};
    outline: none;
    box-shadow: 0 0 0 4px rgba(255, 107, 107, 0.12);
  }

  &.error {
    border-color: ${theme.colors.error};
    box-shadow: 0 0 0 4px ${theme.colors.errorBg};
  }

  &::placeholder {
    color: ${theme.colors.textLight};
  }
`;

const InputIcon = styled.span`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textMuted};
  font-size: 15px;
  pointer-events: none;
`;

const ErrorText = styled.span`
  color: ${theme.colors.error};
  font-size: ${theme.typography.small};
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1.4;
`;

const SubmitButton = styled.button`
  background: ${theme.colors.accentGradient};
  color: white;
  height: 44px;
  border-radius: ${theme.radius.md};
  font-weight: 700;
  font-size: ${theme.typography.body};
  border: none;
  cursor: pointer;
  transition: all ${theme.transitions.base};
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  box-shadow: ${theme.shadows.glowSoft};
  letter-spacing: -0.01em;

  @media (min-width: 1024px) {
    height: 48px;
    margin-top: 12px;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.glow};
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin: 12px 0;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.small};

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${theme.colors.border};
  }
`;

const GoogleSection = styled.div`
  margin: 0 0 8px;
`;

const LoginLink = styled(Link)`
  color: ${theme.colors.primary};
  text-align: center;
  display: block;
  text-decoration: none;
  font-weight: 600;
  transition: all ${theme.transitions.fast};
  padding: 8px;
  border-radius: ${theme.radius.sm};
  font-size: ${theme.typography.body};
  margin-top: 8px;

  &:hover {
    color: ${theme.colors.secondary};
    background: rgba(255, 107, 107, 0.08);
  }
`;

const RequiredAsterisk = styled.span`
  color: ${theme.colors.error};
  margin-left: 2px;
`;

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    phone?: string;
    password?: string;
  }>({});

  const validateForm = (): boolean => {
    const newErrors: {
      name?: string;
      email?: string;
      phone?: string;
      password?: string;
    } = {};

    if (!formData.name) {
      newErrors.name = "Oops! Name field can't be empty.";
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters long.';
    }

    if (!formData.email) {
      newErrors.email = 'Oops! You forgot to add your email.';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone) {
      newErrors.phone = 'Oops! Phone number is missing. ';
    } else if (!/^\+?[\d\s-]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.password) {
      newErrors.password = 'Oops! You forgot to enter your password.';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Too short — your password must be 6 characters minimum.';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(formData.password)) {
      newErrors.password = 'Your password needs to be stronger: add a capital letter, a lowercase letter, a number, and a special symbol.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    try {
      const response = await registerUser(formData);
      console.log("🚀 ~ handleSubmit ~ response:", response)
      const userId = response.data._id;

      // Send OTP after successful registration
      await sendOTP({
        userId,
        email: formData.email
      });

      // Navigate to OTP verification page with required data
      navigate('/verify-otp', {
        state: {
          userId,
          email: formData.email
        }
      });

      toast.success('Registration successful! Please verify your email.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      console.log("Google credential received:", credentialResponse);
      const response = await googleAuth(credentialResponse.credential);
      console.log("Google auth response:", response);

      if (!response) {
        console.error("No response received from googleAuth");
        toast.error('Registration failed: No response from server');
        return;
      }

      const user = response.data.user;
      const token = user.token;

      if (!token) {
        console.error("No token in response data:", response.data);
        toast.error('Registration failed: No token received');
        return;
      }

      if (!user) {
        console.error("No user in response data:", response.data);
        toast.error('Registration failed: No user data received');
        return;
      }

      // Store the token and user data
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      console.log("Token and user data stored successfully");
      toast.success('Google registration successful!');

      // Force a hard navigation to dashboard
      window.location.href = '/dashboard';

    } catch (error) {
      console.error('Google registration error:', error);
      toast.error(`Google registration failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <RegisterContainer>
      <HomeLink to="/">
        <FontAwesomeIcon icon={faHome} /> Back to Home
      </HomeLink>
      <RegisterCard>
        <Title>Create Account</Title>
        <Subtitle>Join us and start your journey today</Subtitle>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>
              Full Name<RequiredAsterisk>*</RequiredAsterisk>
            </Label>
            <InputWrapper>
              <InputIcon>
                <FontAwesomeIcon icon={faUser} />
              </InputIcon>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                className={errors.name ? 'error' : ''}
              />
            </InputWrapper>
            {errors.name && <ErrorText>{errors.name}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <Label>
              Email<RequiredAsterisk>*</RequiredAsterisk>
            </Label>
            <InputWrapper>
              <InputIcon>
                <FontAwesomeIcon icon={faEnvelope} />
              </InputIcon>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                className={errors.email ? 'error' : ''}
              />
            </InputWrapper>
            {errors.email && <ErrorText>{errors.email}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <Label>
              Phone Number<RequiredAsterisk>*</RequiredAsterisk>
            </Label>
            <InputWrapper>
              <InputIcon>
                <FontAwesomeIcon icon={faPhone} />
              </InputIcon>
              <Input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter your phone number"
                className={errors.phone ? 'error' : ''}
              />
            </InputWrapper>
            {errors.phone && <ErrorText>{errors.phone}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <Label>
              Password<RequiredAsterisk>*</RequiredAsterisk>
            </Label>
            <InputWrapper>
              <InputIcon>
                <FontAwesomeIcon icon={faLock} />
              </InputIcon>
              <Input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a password"
                className={errors.password ? 'error' : ''}
              />
            </InputWrapper>
            {errors.password && <ErrorText>{errors.password}</ErrorText>}
          </FormGroup>

          <SubmitButton type="submit" disabled={loading}>
            {loading ? 'Registering...' : 'Register'}
          </SubmitButton>
        </Form>

        <Divider>or continue with</Divider>

        <GoogleSection>
          <GoogleAuthButton onSuccess={handleGoogleSuccess} text="signup_with" />
        </GoogleSection>

        <LoginLink to="/login">
          Already have an account? Sign in
        </LoginLink>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default Register; 